import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Description as FileIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import {
  BankDetailList,
  BankLedgerApi,
  BankLedgerExport,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import { Tooltip } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery, useTheme } from "@mui/material";

const BankLedger = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedRow, setExpandedRow] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [ledgerNameSearch, setLedgerNameSearch] = useState("");

  // ✅ Date Range States (Updated)
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);

  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // ✅ Temporary state for custom date inputs (before applying)
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const [filters, setFilters] = useState({
    date_range: "today",
    ledger_name: "",
  });

  // ✅ Format Date Display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // ✅ Get Date Range Display
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ✅ Get actual date values for API
  const getDateRangeValues = (rangeType) => {
    const now = new Date();
    const formatAPIDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    switch (rangeType) {
      case "today":
        return { from: formatAPIDate(now), to: formatAPIDate(now) };
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatAPIDate(yesterday), to: formatAPIDate(yesterday) };
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return { from: formatAPIDate(weekStart), to: formatAPIDate(now) };
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return {
          from: formatAPIDate(prevWeekStart),
          to: formatAPIDate(prevWeekEnd),
        };
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatAPIDate(monthStart), to: formatAPIDate(now) };
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { from: formatAPIDate(yearStart), to: formatAPIDate(now) };
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return { from: formatAPIDate(fyStart), to: formatAPIDate(now) };
      default:
        return { from: "", to: "" };
    }
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";

    // Take only the date part (before T)
    const datePart = dateTimeString.split("T")[0]; // "2025-12-02"

    const [year, month, day] = datePart.split("-");

    return `${day}-${month}-${year}`;
  }

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  // ✅ Reset Filters
  const handleReset = () => {
    setFilters({
      date_range: "today",
      ledger_name: "", // Add this line
    });
    setLedgerNameSearch(""); // Add this line
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
    setLedgerData([]);
    setPage(1);
    setHasMore(true);
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  // ✅ Auto fetch on filter change
  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedBank) {
        setPage(1);
        fetchLedgerData(1, true);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedBank, filters, customDateRange]);

  const fetchBanks = async () => {
    try {
      const response = await BankDetailList();
      setBanks(response.results || []);
    } catch (error) {
      console.error("Error fetching Banks:", error);
    }
  };

  const fetchLedgerData = async (pageNum = 1, reset = false) => {
    if (!selectedBank) return;

    setLoading(true);

    try {
      const filterValues = {};

      // ✅ Handle date range
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        filterValues.from_date = customDateRange.from;
        filterValues.to_date = customDateRange.to;
        filterValues.date_range = "custom";
      } else if (filters.date_range !== "custom") {
        const dates = getDateRangeValues(filters.date_range);
        filterValues.from_date = dates.from;
        filterValues.to_date = dates.to;
        filterValues.date_range = filters.date_range;
      }
      if (filters.ledger_name) {
        filterValues.ledger_name = filters.ledger_name;
      }

      const response = await BankLedgerApi(
        selectedBank.id,
        filterValues,
        pageNum,
      );

      if (reset || pageNum === 1) {
        if (response.success) {
          setLedgerData(
            Array.isArray(response.entries) ? response.entries : [],
          );
          setSummary(response.summary);
        } else {
          setLedgerData([]);
          setSummary(null);
        }
      } else {
        setLedgerData((prevData) => [
          ...prevData,
          ...(Array.isArray(response.entries) ? response.entries : []),
        ]);
      }

      setHasMore(response.next !== null);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setLedgerData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedBank) {
      toast.warning("⚠️ Please select a bank first");
      return;
    }

    if (!ledgerData || ledgerData.length === 0) {
      toast.warning("⚠️ No data available to export");
      return;
    }

    setExcelLoading(true);
    try {
      const filterValues = {
        date_range: filters.date_range,
      };
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        filterValues.from_date = customDateRange.from;
        filterValues.to_date = customDateRange.to;
      } else if (filters.date_range !== "custom") {
        const dates = getDateRangeValues(filters.date_range);
        filterValues.from_date = dates.from;
        filterValues.to_date = dates.to;
      }
      if (filters.ledger_name) {
        filterValues.ledger_name = filters.ledger_name;
      }

      const blob = await BankLedgerExport(selectedBank.id, filterValues);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bank_Ledger_${selectedBank.bank_name}_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("✅ Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("❌ Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (loading || !hasMore) return;
    setPage((prev) => {
      const next = prev + 1;
      fetchLedgerData(next);
      return next;
    });
  };

  const handleBankChange = (event, newValue) => {
    setSelectedBank(newValue);
    setLedgerData([]);
    setSummary(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  return (
    <Box p={2}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Filters */}
      <Box sx={{ mb: 2, mt: -5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Ledger Name"
              placeholder="Search by ledger name..."
              value={ledgerNameSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLedgerNameSearch(value);
                setFilters((prev) => ({ ...prev, ledger_name: value }));
              }}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* ✅ Date Range Dropdown */}
          <Grid item xs={12} sm={6} md={3.5}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* ✅ Date Range Menu with Custom Date Pickers */}
          <Menu
            anchorEl={dateMenuAnchor}
            open={Boolean(dateMenuAnchor)}
            onClose={() => {
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
              setTempCustomDateRange({ from: "", to: "" });
            }}
            PaperProps={{
              sx: { width: 350, maxHeight: 500 },
            }}
          >
            {/* Today */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: "today" }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">Today</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateRangeDisplay("today")}
                </Typography>
              </Box>
            </MenuItem>

            {/* Yesterday */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: "yesterday" }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">Yesterday</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateRangeDisplay("yesterday")}
                </Typography>
              </Box>
            </MenuItem>

            {/* This Week */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: "this_week" }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">This Week</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay("this_week")}
                </Typography>
              </Box>
            </MenuItem>

            {/* Previous Week */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  date_range: "previous_week",
                }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">Previous Week</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay("previous_week")}
                </Typography>
              </Box>
            </MenuItem>

            {/* This Month */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: "this_month" }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">This Month</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay("this_month")}
                </Typography>
              </Box>
            </MenuItem>

            {/* Current Year */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: "current_year" }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">Current Year</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay("current_year")}
                </Typography>
              </Box>
            </MenuItem>

            {/* Financial Year */}
            <MenuItem
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  date_range: "financial_year",
                }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">Financial Year</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay("financial_year")}
                </Typography>
              </Box>
            </MenuItem>

            <Divider />

            {/* ✅ Custom Date Range - Click to Expand Both Date Pickers */}
            <MenuItem
              onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
              >
                Custom Date Range
              </Typography>
              {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
            </MenuItem>

            {/* ✅ Both Date Pickers Together (Collapsible Section) */}
            <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  bgcolor: "#f5f5f5",
                  borderTop: "1px solid #e0e0e0",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Select Date Range
                </Typography>

                {/* ✅ From and To Date Inputs Side by Side */}
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    value={tempCustomDateRange.from}
                    onChange={(e) =>
                      setTempCustomDateRange((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: tempCustomDateRange.to || undefined,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: 40,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />

                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    value={tempCustomDateRange.to}
                    onChange={(e) =>
                      setTempCustomDateRange((prev) => ({
                        ...prev,
                        to: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: tempCustomDateRange.from || undefined,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: 40,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Box>

                {/* ✅ Show Selected Range Preview */}
                {tempCustomDateRange.from && tempCustomDateRange.to && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 1.5,
                      p: 1,
                      bgcolor: "#e8f5e9",
                      borderRadius: 1,
                      textAlign: "center",
                      color: "#2e7d32",
                      fontWeight: 500,
                    }}
                  >
                    📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                    {formatDateDisplay(tempCustomDateRange.to)}
                  </Typography>
                )}

                {/* ✅ Apply and Cancel Buttons */}
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelCustomDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyCustomDateRange}
                    disabled={
                      !tempCustomDateRange.from || !tempCustomDateRange.to
                    }
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Menu>

          {/* Bank Dropdown */}
          <Grid item xs={12} sm={6} md={3.5}>
            <Autocomplete
              options={banks}
              getOptionLabel={(option) =>
                `${option.bank_name} - ${option.account_number || "N/A"}`
              }
              value={selectedBank}
              onChange={handleBankChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bank"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Reset Button */}
          <Grid item xs={6} sm={3} md={1}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 35,
                fontSize: "0.7rem",
                borderRadius: 0,
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
          {/* Excel Export */}
          <Grid item xs={6} sm={3} md={1}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  color="success"
                  onClick={handleExportExcel}
                  disabled={!ledgerData.length || excelLoading}
                  sx={{ width: "100%", height: 35, borderRadius: 0 }}
                >
                  {excelLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ✅ Summary Cards */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              p: 1,
              border: "2px solid #e0e0e0",
              borderRadius: 1.5,
              bgcolor: "#f5f5f5",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Opening Balance
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.opening_balance || 0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: "2px solid #4caf50",
              borderRadius: 1.5,
              bgcolor: "#e8f5e9",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="success.main"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Total Debit
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="success.main"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.total_debit || 0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: "2px solid #f44336",
              borderRadius: 1.5,
              bgcolor: "#ffebee",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="error.main"
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Total Credit
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="error.main"
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.total_credit || 0)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1,
              border: `2px solid ${(summary?.closing_balance || 0) >= 0 ? "#4caf50" : "#f44336"}`,
              borderRadius: 1.5,
              bgcolor:
                (summary?.closing_balance || 0) >= 0 ? "#e8f5e9" : "#ffebee",
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color={
                (summary?.closing_balance || 0) >= 0
                  ? "success.main"
                  : "error.main"
              }
              fontWeight="bold"
              fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
            >
              Closing Balance
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color={
                (summary?.closing_balance || 0) >= 0
                  ? "success.main"
                  : "error.main"
              }
              fontSize={{ xs: "0.85rem", sm: "1rem" }}
            >
              {formatCurrency(summary?.closing_balance || 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ✅ CONDITIONAL: Mobile Cards | Desktop Table */}
      {isMobile ? (
        /* 📱 MOBILE VIEW - Expandable Dropdown Cards */
        <Box
          id="scrollableDiv"
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <InfiniteScroll
            dataLength={ledgerData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <h4 style={{ textAlign: "center", fontSize: "0.75rem" }}>
                Loading...
              </h4>
            }
            scrollableTarget="scrollableDiv"
            endMessage={
              <p
                style={{
                  textAlign: "center",
                  padding: "10px",
                  fontSize: "0.75rem",
                }}
              >
                No more vouchers!
              </p>
            }
          >
            {/* Loading State */}
            {loading && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <CircularProgress size={30} />
                <Typography
                  sx={{ mt: 1, fontSize: "0.75rem", color: "text.secondary" }}
                >
                  Loading...
                </Typography>
              </Box>
            )}

            {/* No Bank Selected */}
            {!loading && !selectedBank && (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                >
                  Please select a bank to view ledger entries
                </Typography>
              </Box>
            )}

            {/* Bank Selected but No Data */}
            {!loading && selectedBank && ledgerData.length === 0 && (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "#fff3e0",
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ fontSize: "0.8rem", color: "warning.main" }}>
                  No ledger entries found for this bank
                </Typography>
              </Box>
            )}

            {/* Data Cards */}
            {!loading &&
              ledgerData.length > 0 &&
              ledgerData.map((entry, index) => (
                <Box key={entry.id}>
                  {/* Main Row - Always Visible */}
                  <Box
                    onClick={() =>
                      setExpandedRow(expandedRow === index ? null : index)
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": {
                        bgcolor: "#e3f2fd",
                      },
                      borderLeft: `4px solid ${entry.debit > 0 ? "#f44336" : "#4caf50"}`,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      {/* Date & Voucher Type */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
                        >
                          {formatOnlyDate(entry.date)}
                        </Typography>
                        <Chip
                          label={entry.voucher_type}
                          color={entry.debit > 0 ? "error" : "success"}
                          size="small"
                          sx={{ fontSize: "0.6rem", height: 18 }}
                        />
                      </Box>

                      {/* Voucher Number */}
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "#666", mb: 0.3 }}
                      >
                        {entry.voucher_number || "N/A"}
                      </Typography>

                      {/* Amount */}
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          color:
                            entry.debit > 0 ? "error.main" : "success.main",
                        }}
                      >
                        {entry.debit > 0
                          ? `${formatCurrency(entry.debit)} Dr`
                          : `${formatCurrency(entry.credit)} Cr`}
                      </Typography>
                    </Box>

                    {/* Expand Icon */}
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedRow === index ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Expanded Details */}
                  <Collapse
                    in={expandedRow === index}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: `4px solid ${entry.debit > 0 ? "#f44336" : "#4caf50"}`,
                      }}
                    >
                      {/* Description */}
                      <Box sx={{ mb: 0.8 }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DESCRIPTION
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {entry.description || "N/A"}
                        </Typography>
                      </Box>

                      {/* Debit & Credit */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                          mb: 0.8,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            DEBIT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: entry.debit > 0 ? "error.main" : "#999",
                            }}
                          >
                            {entry.debit > 0
                              ? formatCurrency(entry.debit)
                              : "-"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            CREDIT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: entry.credit > 0 ? "success.main" : "#999",
                            }}
                          >
                            {entry.credit > 0
                              ? formatCurrency(entry.credit)
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Balance */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          BALANCE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color:
                              entry.balance >= 0
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {formatCurrency(entry.balance)}
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>

                  <Divider />
                </Box>
              ))}
          </InfiniteScroll>
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW - Original Table */
        <TableContainer
          component={Paper}
          id="scrollableDiv"
          sx={{
            maxHeight: "88vh",
            overflow: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <InfiniteScroll
            dataLength={ledgerData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<div>Loading...</div>}
            endMessage={
              <p style={{ textAlign: "center" }}>No more vouchers!</p>
            }
            scrollableTarget="scrollableDiv"
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1000,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "2px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Voucher Type</TableCell>
                  <TableCell>Voucher No</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                  <TableCell>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Loading State */}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                )}

                {/* No Bank Selected */}
                {!loading && !selectedBank && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        Please select a bank to view ledger entries
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Bank Selected but No Data */}
                {!loading && selectedBank && ledgerData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        No ledger entries found for this bank
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Data Rows */}
                {!loading &&
                  ledgerData.length > 0 &&
                  ledgerData.map((entry, index) => (
                    <TableRow
                      hover
                      key={entry.id}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "& td": {
                          paddingTop: "4px !important",
                          paddingBottom: "4px !important",
                          fontSize: "0.8125rem",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatOnlyDate(entry.date)}</TableCell>
                      <TableCell>{entry.voucher_type}</TableCell>
                      <TableCell>{entry.voucher_number}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Tooltip title={entry.description || "-"} arrow>
                          <span>
                            {truncateText(entry.description || "-", 20)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            entry.debit > 0 ? "error.main" : "text.disabled",
                        }}
                      >
                        {entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                      </TableCell>
                      <TableCell>
                        {entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                      </TableCell>
                      <TableCell>{formatCurrency(entry.balance)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </InfiniteScroll>
        </TableContainer>
      )}
    </Box>
  );
};

export default BankLedger;
